"use client";

import * as React from "react";
import { Check, ShoppingCart } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { faNum, faToman } from "@/lib/format";

export type Plan = {
  id: string;
  name: string;
  description: string;
  priceToman: number;
  durationDays: number;
  trafficGB: number;
  popular: boolean;
  active: boolean;
  sortOrder: number;
};

function durationLabel(days: number): string {
  if (days >= 60) return `${faNum(Math.round(days / 30))} ماهه`;
  return `${faNum(days)} روزه`;
}

function trafficLabel(gb: number): string {
  return gb === 0 ? "نامحدود" : `${faNum(gb)} گیگابایت`;
}

export function PlanPicker({
  plans,
  onBuy,
}: {
  plans: Plan[];
  onBuy: (planId: string) => void;
}) {
  if (plans.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
        در حال حاضر طرحی برای فروش موجود نیست.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {plans.map((plan) => (
        <PlanCard key={plan.id} plan={plan} onBuy={onBuy} />
      ))}
    </div>
  );
}

function PlanCard({
  plan,
  onBuy,
}: {
  plan: Plan;
  onBuy: (planId: string) => void;
}) {
  return (
    <article
      className={cn(
        "relative flex flex-col gap-4 rounded-2xl border bg-card p-6 transition",
        plan.popular
          ? "border-primary ring-2 ring-primary/30 shadow-lg"
          : "border-border hover:border-primary/40 hover:shadow-md",
      )}
    >
      {plan.popular && (
        <Badge className="absolute -top-3 right-5 shadow-sm">محبوب‌ترین</Badge>
      )}
      <div className="space-y-1">
        <h3 className="text-xl font-bold">{plan.name}</h3>
        <p className="text-sm text-muted-foreground">
          {durationLabel(plan.durationDays)}
        </p>
      </div>
      <div className="space-y-1.5 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Check className="size-4 text-primary" /> ترافیک: {trafficLabel(plan.trafficGB)}
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Check className="size-4 text-primary" /> مدت: {durationLabel(plan.durationDays)}
        </div>
      </div>
      {plan.description ? (
        <p className="text-sm text-muted-foreground">{plan.description}</p>
      ) : null}
      <div className="mt-auto space-y-3 pt-2">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-extrabold">
            {faToman(plan.priceToman)}
          </span>
        </div>
        <Button
          className="w-full gap-2"
          variant={plan.popular ? "default" : "outline"}
          onClick={() => onBuy(plan.id)}
        >
          <ShoppingCart className="size-4" /> خرید
        </Button>
      </div>
    </article>
  );
}

export default PlanPicker;
